// Implements: F-UIX-018, F-USG-006
/**
 * "Coste por mockup" of My usage (UIX.4, USG.19): the person's own spend per mockup over
 * the report's range. Pure presentation; money is formatted here only (OD-12).
 *
 * Rows are keyed by mockup id and currency (checklist 14). A mockup the person may open
 * links to it; one the server reads as `"Mockup no disponible"` (deleted, in the trash or
 * out of reach) is plain text, with its cost kept so the totals reconcile.
 *
 * Mockup v3 P7: when the container gives `renderBreakdown`, each row opens its cost by version
 * in a row under it (a disclosure button: `aria-expanded`, `aria-controls`, named for the
 * mockup). Which rows are open is kept by row key, so opening or closing one never touches
 * another. A mockup that no longer exists opens the sentence that says so and reads nothing.
 */

import { useId, useState } from "react";
import type { ReactElement, ReactNode } from "react";
import { Link } from "react-router";

import { useTranslation } from "../../../shared/i18n";
import { moneyText } from "../../../shared/ui/moneyText";
import { EmptyState } from "../../../shared/ui/molecules/EmptyState";
import { Heading } from "../../../shared/ui/atoms/Heading";
import { Icon } from "../../../shared/ui/atoms/Icon";
import type { CostedMockupView, MyMockupsView } from "../../../types/reports";
import styles from "./MyUsage.module.css";

/** The columns of a row: the toggle (when present), mockup, generations, delivered, failed, cost. */
const COLUMNS = 5;

export interface MyMockupCostsProps {
  readonly mockups: MyMockupsView;
  /** The empty state's sentence; My usage's own by default. */
  readonly emptyBody?: string;
  /**
   * The list heading's level, one under its container's: `2` on My usage (under the page), `3`
   * inside a scope view (under the target's heading). The empty state's title sits one deeper.
   */
  readonly headingLevel?: 2 | 3;
  /**
   * The period the list covers, as the person reads it ("Del … al …."), shown under the heading
   * so its costs are never read as the breakdown's lifetime totals (verifier, lane 28).
   */
  readonly rangeText?: string | undefined;
  /** The cost by version of an available mockup; without it the rows open nothing. */
  readonly renderBreakdown?: ((mockup: CostedMockupView) => ReactNode) | undefined;
}

export function MyMockupCosts({
  mockups,
  emptyBody,
  headingLevel = 2,
  rangeText,
  renderBreakdown,
}: MyMockupCostsProps): ReactElement {
  const { t } = useTranslation();
  const headingId = useId();
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set());
  const expandable = renderBreakdown !== undefined;

  function toggle(key: string): void {
    setOpen((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  return (
    <section className={styles.section} aria-labelledby={headingId}>
      <Heading level={headingLevel} id={headingId} className={styles.sectionTitle}>
        {t("backoffice.myUsage.mockups.title")}
      </Heading>
      {rangeText === undefined ? null : <p className={styles.hint}>{rangeText}</p>}
      {mockups.rows.length === 0 ? (
        <EmptyState
          headingLevel={headingLevel === 2 ? 3 : 4}
          title={t("backoffice.myUsage.mockups.emptyTitle")}
          body={emptyBody ?? t("backoffice.myUsage.mockups.emptyBody")}
        />
      ) : (
        <table className={styles.table} aria-labelledby={headingId}>
          <thead>
            <tr>
              {expandable ? (
                <th scope="col" className={styles.toggleColumn}>
                  <span className={styles.srOnly}>{t("cost.breakdown.toggleColumn")}</span>
                </th>
              ) : null}
              <th scope="col">{t("backoffice.myUsage.column.mockup")}</th>
              <th scope="col">{t("backoffice.myUsage.column.generations")}</th>
              <th scope="col">{t("backoffice.myUsage.column.delivered")}</th>
              <th scope="col">{t("backoffice.myUsage.column.failed")}</th>
              <th scope="col">{t("backoffice.myUsage.column.cost")}</th>
            </tr>
          </thead>
          <tbody>
            {mockups.rows.map((row, index) => {
              const key = `${row.mockup.id}:${row.currency}`;
              const isOpen = expandable && open.has(key);
              const panelId = `${headingId}-breakdown-${String(index)}`;
              return [
                <tr key={key}>
                  {expandable ? (
                    <td className={styles.toggleColumn}>
                      <button
                        type="button"
                        className={styles.toggle}
                        aria-expanded={isOpen}
                        aria-controls={isOpen ? panelId : undefined}
                        aria-label={
                          isOpen
                            ? t("cost.breakdown.hide", { name: row.mockup.name })
                            : t("cost.breakdown.show", { name: row.mockup.name })
                        }
                        onClick={() => {
                          toggle(key);
                        }}
                      >
                        <Icon name="chevron-down" size="sm" />
                      </button>
                    </td>
                  ) : null}
                  <th scope="row">
                    {row.mockup.status === "available" ? (
                      <Link className={styles.link} to={`/lienzo/${encodeURIComponent(row.mockup.id)}`}>
                        {row.mockup.name}
                      </Link>
                    ) : (
                      row.mockup.name
                    )}
                  </th>
                  <td>{row.generations}</td>
                  <td>{row.delivered}</td>
                  <td>{row.failed}</td>
                  <td>{moneyText(row.cost_micros, row.currency)}</td>
                </tr>,
                isOpen ? (
                  <tr key={`${key}:breakdown`} className={styles.subRow}>
                    <td colSpan={COLUMNS + 1} id={panelId}>
                      {row.mockup.status === "available" ? (
                        renderBreakdown(row.mockup)
                      ) : (
                        <p className={styles.hint}>{t("cost.breakdown.gone")}</p>
                      )}
                    </td>
                  </tr>
                ) : null,
              ];
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}
